import Page from '@/components/Page'
import PageLoading from '@/components/PageLoading'
import { Tab } from '@/components/Sidebar/config'
import { getIsNotiAllowed, setIsNotiAllowed, useIsNotiAllowed } from '@/states/notification'
import { scrollToTop } from '@/utils/dom'
import {
	NOTIFICATIONS_QUERY_OPTIONS,
	UNREAD_NOTIFICATION_COUNT_QUERY_OPTIONS,
} from '@/utils/fetchers/notification'
import useTabClickListener from '@/utils/hooks/useTabClickListener'
import { prepareToRefetchListQuery } from '@/utils/list'
import { sleep } from '@/utils/promise'
import queryClient from '@/utils/queryClient'
import { css } from '@emotion/react'
import { Button } from '@ruguoapp/jike-fe-components'
import { BellOnOutlineIcon } from '@ruguoapp/jike-fe-icons'
import { useQuery, useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { flatMap } from 'lodash-es'
import { type HTMLAttributes, useCallback, useMemo } from 'react'
import toast from 'react-hot-toast'
import { useMount, useTitle } from 'react-use'
import NotificationList from '../components/Notifications/NotificationList'

export const Route = createFileRoute('/notification')({
	component: NotificationPage,
	pendingComponent: () => <PageLoading />,
	loader: async () => {
		await queryClient.ensureInfiniteQueryData(NOTIFICATIONS_QUERY_OPTIONS)
	},
	preload: false,
})

export interface NotificationsPageProps extends HTMLAttributes<HTMLDivElement> {}

export default function NotificationPage({ ...props }: NotificationsPageProps) {
	useTitle(`通知 - 即刻`)

	const {
		data: queryData,
		fetchNextPage,
		refetch,
		isFetching,
	} = useSuspenseInfiniteQuery(NOTIFICATIONS_QUERY_OPTIONS)
	const { data: unreadCount = 0 } = useQuery(UNREAD_NOTIFICATION_COUNT_QUERY_OPTIONS)
	const data = useMemo(() => [...flatMap(queryData?.pages, 'data')], [queryData])

	const refresh = useCallback(async () => {
		prepareToRefetchListQuery(NOTIFICATIONS_QUERY_OPTIONS.queryKey)
		return refetch()
			.then(() =>
				queryClient.invalidateQueries({
					queryKey: UNREAD_NOTIFICATION_COUNT_QUERY_OPTIONS.queryKey,
				})
			)
			.then(() => {
				toast.success('加载成功')
			})
	}, [refetch])

	const [isNotiAllow] = useIsNotiAllowed()
	const handleNotiAllowanceChange = useCallback(() => {
		const isAllow = getIsNotiAllowed()
		if (isAllow) {
			setIsNotiAllowed(false)
			toast('通知已关闭')
		} else {
			Notification.requestPermission().then((permission) => {
				const isGranted = permission === 'granted'
				setIsNotiAllowed(isGranted)
				toast(isGranted ? '通知已开启' : '通知已关闭')
			})
		}
	}, [])

	useMount(() => {
		if (unreadCount > 0) {
			sleep(1000)
				.then(() => scrollToTop())
				.then(() => sleep(500))
				.then(() => refresh())
		}
	})

	useTabClickListener(Tab.NOTIFICATION, refresh)

	return (
		<Page
			title="通知"
			onReachPageBottom={fetchNextPage}
			right={
				<Button
					appearance={isNotiAllow ? 'primary' : 'ghost'}
					size="sm"
					css={css`
						display: flex;
						align-items: center;
						margin-left: auto;
					`}
					onClick={handleNotiAllowanceChange}
				>
					<BellOnOutlineIcon color="currentColor" width={20} height={20} />
				</Button>
			}
		>
			<NotificationList data={data} isFetching={isFetching} />
		</Page>
	)
}
